
import React from "react";
import { Helmet } from "react-helmet-async";
import { getAreaBySlug } from "@/data/londonAreaCoordinates";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  structuredData?: Record<string, any>;
  keywords?: string;
  pageType?: 'homepage' | 'blog' | 'service' | 'location' | 'product' | 'contact' | 'about' | 'default';
  faqs?: Array<{ question: string; answer: string }>;
  noIndex?: boolean;
  /** ISO date the article was first published (blog pages) */
  datePublished?: string;
  /** ISO date the article was last substantively updated */
  dateModified?: string;
  /** Named author for article schema; falls back to the organisation */
  author?: string;
}

export function SEO({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage = "/og-image.png",
  structuredData,
  keywords = "secondary glazing, london, noise reduction, thermal insulation, heritage windows, energy efficiency",
  pageType = 'default',
  faqs,
  noIndex = false,
  datePublished,
  dateModified,
  author,
}: SEOProps) {
  const siteTitle = "Secondary Glazing London";
  const CANONICAL_ORIGIN = 'https://www.londonsecondaryglazing.co.uk';

  // Always emit a canonical. If the caller passes one, normalise it to the
  // canonical origin (www, https, no trailing slash). Otherwise derive it
  // from the current URL so every route gets a consistent canonical tag.
  const buildCanonical = (input?: string): string => {
    let pathname = '/';
    if (input) {
      try {
        const u = new URL(input, CANONICAL_ORIGIN);
        pathname = u.pathname + u.search;
      } catch {
        pathname = input.startsWith('/') ? input : `/${input}`;
      }
    } else if (typeof window !== 'undefined') {
      pathname = window.location.pathname + window.location.search;
    }
    // Strip trailing slash (except root) to match the site-wide 301 policy.
    if (pathname.length > 1 && pathname.endsWith('/')) {
      pathname = pathname.replace(/\/+$/, '');
    }
    return `${CANONICAL_ORIGIN}${pathname}`;
  };

  const normalizedCanonical = buildCanonical(canonical);
  // Most service and location titles already contain the primary brand phrase.
  // Avoid adding a repetitive suffix that pushes local titles past display limits.
  const fullTitle = title.toLowerCase().includes("secondary glazing")
    ? title
    : `${title} | ${siteTitle}`;
  

  // ---------------------------------------------------------------------------
  // Entity graph: every schema node hangs off two stable @ids so Google merges
  // the organisation and site into single entities instead of many duplicates.
  // ---------------------------------------------------------------------------
  const ORG_ID = `${CANONICAL_ORIGIN}/#organization`;
  const SITE_ID = `${CANONICAL_ORIGIN}/#website`;
  const BUSINESS_ID = `${CANONICAL_ORIGIN}/#business`;
  const PHONE = "+442070601572";
  const LOGO_URL = `${CANONICAL_ORIGIN}/lovable-uploads/8d597530-1488-43a6-93e9-64077dcedf87.png`;

  // Schema.org requires absolute URLs for images; callers pass site-relative paths.
  const absUrl = (path?: string): string => {
    if (!path) return LOGO_URL;
    if (/^https?:\/\//i.test(path)) return path;
    return `${CANONICAL_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
  };

  const absoluteOgImage = absUrl(ogImage);

  const POSTAL_ADDRESS = {
    "@type": "PostalAddress",
    "streetAddress": "Arch 141, MacFarlane Road",
    "addressLocality": "London",
    "addressRegion": "Greater London",
    "postalCode": "W12 7LA",
    "addressCountry": "GB",
  };

  const OPENING_HOURS = [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "08:00",
      "closes": "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Saturday"],
      "opens": "09:00",
      "closes": "14:00",
    },
  ];

  const organizationNode = {
    "@type": "Organization",
    "@id": ORG_ID,
    "name": "London Secondary Glazing",
    "url": `${CANONICAL_ORIGIN}/`,
    "logo": {
      "@type": "ImageObject",
      "@id": `${CANONICAL_ORIGIN}/#logo`,
      "url": LOGO_URL,
      "caption": "London Secondary Glazing",
    },
    "image": { "@id": `${CANONICAL_ORIGIN}/#logo` },
    "telephone": PHONE,
    "email": "info@londonsecondaryglazing.co.uk",
    "address": POSTAL_ADDRESS,
    "areaServed": { "@type": "City", "name": "London" },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": PHONE,
      "contactType": "customer service",
      "areaServed": "GB",
      "availableLanguage": "English",
    },
    "sameAs": [
      "https://www.facebook.com/londonsecondaryglazing",
      "https://www.instagram.com/londonsecondaryglazing",
    ],
  };

  const websiteNode = {
    "@type": "WebSite",
    "@id": SITE_ID,
    "url": `${CANONICAL_ORIGIN}/`,
    "name": "London Secondary Glazing",
    "description": "Bespoke secondary glazing for London period, listed and heritage properties.",
    "inLanguage": "en-GB",
    "publisher": { "@id": ORG_ID },
  };

  const businessNode = {
    "@type": "HomeAndConstructionBusiness",
    "@id": BUSINESS_ID,
    "name": "London Secondary Glazing",
    "description": "Professional secondary glazing solutions in London for noise reduction, thermal insulation, and heritage preservation.",
    "url": `${CANONICAL_ORIGIN}/`,
    "image": { "@id": `${CANONICAL_ORIGIN}/#logo` },
    "logo": LOGO_URL,
    "telephone": PHONE,
    "email": "info@londonsecondaryglazing.co.uk",
    "address": POSTAL_ADDRESS,
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "51.507351",
      "longitude": "-0.127758",
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "51.507351",
        "longitude": "-0.127758",
      },
      "geoRadius": "40000",
    },
    "openingHoursSpecification": OPENING_HOURS,
    "priceRange": "££-£££",
    "currenciesAccepted": "GBP",
    "paymentAccepted": "Cash, Credit Card, Debit Card, Bank Transfer",
    "parentOrganization": { "@id": ORG_ID },
    "sameAs": organizationNode.sameAs,
  };

  const webPageNode: Record<string, any> = {
    "@type": pageType === 'contact' ? "ContactPage" : pageType === 'blog' ? "WebPage" : "WebPage",
    "@id": `${normalizedCanonical}#webpage`,
    "url": normalizedCanonical,
    "name": fullTitle,
    "description": description,
    "isPartOf": { "@id": SITE_ID },
    "about": { "@id": ORG_ID },
    "primaryImageOfPage": {
      "@type": "ImageObject",
      "url": absoluteOgImage,
    },
    "inLanguage": "en-GB",
    // Voice-assistant and AI-answer surfaces read the headline and intro first.
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", "main p"],
    },
  };

  const faqNode = faqs && faqs.length > 0 ? {
    "@type": "FAQPage",
    "@id": `${normalizedCanonical}#faq`,
    "isPartOf": { "@id": `${normalizedCanonical}#webpage` },
    "inLanguage": "en-GB",
    "mainEntity": faqs.map((faq, i) => ({
      "@type": "Question",
      "@id": `${normalizedCanonical}#question-${i + 1}`,
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  } : null;

  // Type-specific nodes, all pointing back at the shared organisation entity.
  const getPageNodes = (): Record<string, any>[] => {
    switch (pageType) {
      case 'blog':
        return [{
          "@type": "BlogPosting",
          "@id": `${normalizedCanonical}#article`,
          "headline": title,
          "description": description,
          "image": {
            "@type": "ImageObject",
            "url": absoluteOgImage,
          },
          "url": normalizedCanonical,
          "mainEntityOfPage": { "@id": `${normalizedCanonical}#webpage` },
          "datePublished": datePublished,
          "dateModified": dateModified || datePublished,
          "author": author
            ? { "@type": "Person", "name": author, "worksFor": { "@id": ORG_ID } }
            : { "@id": ORG_ID },
          "publisher": { "@id": ORG_ID },
          "inLanguage": "en-GB",
          "isPartOf": { "@id": SITE_ID },
          "keywords": keywords,
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": ["h1", "article p"],
          },
        }];

      case 'service':
        return [{
          "@type": "Service",
          "@id": `${normalizedCanonical}#service`,
          "name": title,
          "description": description,
          "serviceType": "Secondary glazing supply and installation",
          "provider": { "@id": BUSINESS_ID },
          "areaServed": { "@type": "City", "name": "London" },
          "url": normalizedCanonical,
          "termsOfService": `${CANONICAL_ORIGIN}/terms-conditions`,
          "offers": {
            "@type": "Offer",
            "priceCurrency": "GBP",
            "availability": "https://schema.org/InStock",
            "url": normalizedCanonical,
            "seller": { "@id": ORG_ID },
          },
        }, businessNode];

      case 'location': {
        const locationName = title.includes("Secondary Glazing")
          ? title.split("Secondary Glazing")[0].trim()
          : "London";
        const slug = normalizedCanonical.split('/').pop() || '';
        const areaData = getAreaBySlug(slug);

        return [{
          "@type": "HomeAndConstructionBusiness",
          "@id": `${normalizedCanonical}#localbusiness`,
          "name": `London Secondary Glazing – ${locationName}`,
          "description": description,
          "url": normalizedCanonical,
          "image": absoluteOgImage,
          "telephone": PHONE,
          "email": "info@londonsecondaryglazing.co.uk",
          "address": {
            ...POSTAL_ADDRESS,
            "addressRegion": areaData?.borough || "Greater London",
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": String(areaData?.lat ?? "51.507351"),
            "longitude": String(areaData?.lng ?? "-0.127758"),
          },
          "areaServed": {
            "@type": "Place",
            "name": locationName,
            ...(areaData ? {
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": String(areaData.lat),
                "longitude": String(areaData.lng),
              },
            } : {}),
          },
          "priceRange": "££-£££",
          "currenciesAccepted": "GBP",
          "paymentAccepted": "Cash, Credit Card, Debit Card, Bank Transfer",
          "openingHoursSpecification": OPENING_HOURS,
          "parentOrganization": { "@id": ORG_ID },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": `Secondary Glazing Services in ${locationName}`,
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": `Acoustic secondary glazing in ${locationName}`,
                  "description": `Acoustic secondary glazing reducing traffic and neighbour noise by up to 80% in ${locationName}.`,
                },
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": `Thermal secondary glazing in ${locationName}`,
                  "description": `Low-E secondary glazing cutting heat loss and draughts in ${locationName} properties.`,
                },
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": `Heritage and listed building glazing in ${locationName}`,
                  "description": `Reversible, conservation-friendly secondary glazing for listed buildings in ${areaData?.borough || locationName}.`,
                },
              },
            ],
          },
        }];
      }

      case 'product':
        return [{
          "@type": "Product",
          "@id": `${normalizedCanonical}#product`,
          "name": title,
          "description": description,
          "image": absoluteOgImage,
          "brand": { "@type": "Brand", "name": "London Secondary Glazing" },
          "category": "Secondary glazing units",
          "url": normalizedCanonical,
          // Bespoke, measured-to-order product: an AggregateOffer with a real
          // range is accurate where a single fixed price would not be.
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "GBP",
            "lowPrice": "430",
            "highPrice": "1750",
            "offerCount": "7",
            "availability": "https://schema.org/InStock",
            "url": normalizedCanonical,
            "seller": { "@id": ORG_ID },
            "areaServed": { "@type": "City", "name": "London" },
          },
        }, businessNode];

      case 'contact':
        return [businessNode];

      case 'homepage':
        return [businessNode];

      default:
        return [businessNode];
    }
  };

  const customStructuredNodes = structuredData
    ? Array.isArray(structuredData["@graph"])
      ? structuredData["@graph"]
      : [{ ...structuredData, "@context": undefined }]
    : [];

  const finalStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      organizationNode,
      websiteNode,
      webPageNode,
      ...getPageNodes(),
      ...customStructuredNodes,
      ...(faqNode ? [faqNode] : []),
    ],
  };


  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      
      {/* Keywords and additional meta tags */}
      <meta name="keywords" content={keywords} />
      <meta name="author" content="London Secondary Glazing" />
      <meta name="robots" content={noIndex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="googlebot" content={noIndex ? "noindex, follow" : "index, follow, max-video-preview:-1, max-image-preview:large, max-snippet:-1"} />
      <meta name="bingbot" content={noIndex ? "noindex, follow" : "index, follow, max-video-preview:-1, max-image-preview:large, max-snippet:-1"} />
      
      {/* Geographic meta tags */}
      <meta name="geo.region" content="GB-LND" />
      <meta name="geo.placename" content="London" />
      <meta name="ICBM" content="51.5074, -0.1278" />
      
      {/* Additional optimization meta tags */}
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="revisit-after" content="7 days" />
      <meta name="language" content="en-GB" />
      <meta name="copyright" content="London Secondary Glazing" />
      
      {/* Preconnect and DNS prefetch for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      
      {/* Canonical URL — always emitted, normalised to www domain, no trailing slash */}
      <link rel="canonical" href={normalizedCanonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:url" content={normalizedCanonical} />
      <meta property="og:site_name" content="London Secondary Glazing" />
      <meta property="og:locale" content="en_GB" />
      
      {/* Optional additional Open Graph tags */}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="London Secondary Glazing solutions" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteOgImage} />
      <meta name="twitter:site" content="@ldnsecondaryglazing" />
      <meta name="twitter:creator" content="@ldnsecondaryglazing" />
      
      {/* Structured data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </Helmet>
  );
}
